import { Component, OnDestroy, OnInit } from '@angular/core';
import { ProductionJob, ProductionJobStatus } from '../../../models/review/production-job.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { UserConfigs } from '../../../models/user-configs.model';
import { UserConfigsService } from '../../../services/user-configs.service';
import { HelpService } from '../../../services/help.service';
import { ProcessingQueueRequest } from '../../../models/processing-queue-request.model';
import { ProcessingQueueService } from '../../../services/processing-queue.service';
import { Role } from 'src/app/models/role.model';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { UntypedFormControl } from '@angular/forms';
import { RunProductionDialogComponent } from '../dialogs/run-production-dialog/run-production-dialog.component';
import { OperationsProductionDownloadDialogComponent } from '../dialogs/operations-production-download-dialog/operations-production-download-dialog.component';
import { DownloadProductionDialogComponent } from '../dialogs/download-production-dialog/download-production-dialog.component';
import { UserResource } from '../../../models/resource/user.resource';
import { ProductionWithheldManager } from '../../../managers/production-withheld.manager';
import { ProductionWithheldLogDialogComponent } from '../dialogs/production-withheld-log-dialog/production-withheld-log-dialog.component';
import { DownloadProductionWithheldDialogComponent } from '../dialogs/download-production-withheld-dialog/download-production-withheld-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RunProductionJobRequest } from '../../../models/ui/job-request/run-production-job-request.model';
import { ProcessingManager } from '../../../managers/processing.manager';
import { SelectedRangeEvent } from '../bates-range-select/bates-range-select.component';
import { NotificationHubSdk } from 'src/app/sdks/notification.hub-sdk';
import { BaseComponent } from '../../base.component';
import { ProductionJobSdk } from '../../../sdks/production-job.sdk';
import { UserManager } from '../../../managers/user.manager';
import { ProductionJobView } from '@models/ui/production-job-view.model';
import { CaseHubSdk } from "@sdks/case.hub-sdk";
import { Store } from "@ngrx/store";
import { selectIsProcessing } from "@store/global/global.selectors";
import { ComparisonUtility } from '@utilities/comparison.utility';

@Component({
    selector: 'lex-production-job',
    templateUrl: './production-job.component.html',
    styleUrls: ['./production-job.component.scss'],
    standalone: false
})
export class ProductionJobComponent extends BaseComponent implements OnInit, OnDestroy {

	public editJobId: string = null;
	public selectedJob: ProductionJobView = null;
	public selectedJob$: Subject<ProductionJobView>;
	public productionJobs: ProductionJobView[] = new Array<ProductionJobView>();
	public userConfig: UserConfigs;

	public validateProduction: boolean = false;
	public showCreateProductionView: boolean = false;
	public showBatesRangeView: boolean = false;
	public isJobValid: boolean;
	public isProcessing: boolean = false;
	public hasPrivilegeLog: boolean = false;
	public userAlert: string;
	public users: UserResource[] = [];
	public productionJobRunStatus = ProductionJobStatus;
	public productionDownloadPath: UntypedFormControl = new UntypedFormControl(['']);

	get Role(): typeof Role {
		return Role;
	}

	private subscription = new Subscription();
	private convertToTiffProductionFunctionId: string = 'da849e9e-08df-4c01-9682-c8be67d3be28';

	constructor(public userConfigService: UserConfigsService,
		private dialog: MatDialog,
		private userManager: UserManager,
		private processingService: ProcessingQueueService,
		private notificationHubSdk: NotificationHubSdk,
		private caseHubSdk: CaseHubSdk,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private helpService: HelpService,
		private productionWithheldManager: ProductionWithheldManager,
		private matSnackBar: MatSnackBar,
		private processingManager: ProcessingManager,
		private productionJobSdk: ProductionJobSdk,
		private store: Store
	) {
		super()
	}

	public ngOnInit(): void {
		this.helpService.updateHelpLink('discovery-menu/production-job');
		this.selectedJob$ = new Subject<ProductionJobView>();
		this.selectedJob$.pipe(
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			takeUntil(this.destroy$)
		).subscribe(data => this.setJob(data));
		this.userConfigService.getUserConfigs().subscribe((config: UserConfigs) => {
			this.userConfig = config
		});
		this.evaluateProcessing();
		this.initializeProductionJobs();
		this.caseHubSdk.productionJobs$.pipe(
			takeUntil(this.destroy$)
		).subscribe(() => this.initializeProductionJobs());
	}

	public ngOnDestroy() {
		super.ngOnDestroy();
		this.subscription.unsubscribe();
	}

	public forceCreateZip(id: string): void {
		this.productionJobSdk.forceCreateZip(id).subscribe();
	}

	public isSelectedJob(job: ProductionJobView): boolean {
		if (!this.selectedJob)
			return false;

		return job.productionJobId == this.selectedJob.productionJobId;
	}

	public createJob() {
		if (this.productionJobs.length > 0) {
			this.selectedJob$.next(null);
			this.showCreateProductionView = true;
			this.showBatesRangeView = false;
			return;
		}

		// First Production of the case.
		let newJob = new ProductionJobView();
		newJob.title = `Production ${formatDate(new Date(), 'MM-dd-yyyy', 'en-US')}`;
		newJob.createdById = this.userConfig.userId;
		newJob.caseId = this.userConfig.currentCase.caseId;
		newJob.batesBasePrefix = '';
		newJob.batesBaseNumberStart = 1;
		newJob.batesBaseNumberDigits = 7;
		newJob.isBatesStampPages = true;
		this.productionJobSdk.createProduction(newJob)
			.pipe(
				switchMap(data => {
					newJob = data;
					return this.userManager.getProductionJobUsers()
						.pipe(map(users => ({
							newJob,
							users
						})));
				}))
			.subscribe(data => {
				this.users = data.users;
				this.productionJobs.unshift(data.newJob);
				this.selectedJob$.next(data.newJob);
			});
	}

	public patchJobTitle(job: ProductionJob) {
		this.productionJobSdk.patch(job.productionJobId, { 'title': job.title })
			.pipe(
				switchMap((jobResponse: ProductionJob) => {
					const idx = this.productionJobs.findIndex(p => p.productionJobId === job.productionJobId);
					this.productionJobs[idx] = jobResponse;
					return of(jobResponse);
				}))
			.subscribe();
		this.editJobId = '';
		this.setJob(job);
	}

	public deleteJob(job: ProductionJob) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = {
			title: `Delete Production Job`,
			message: `Delete
      ${job.title}?
          <br/> The job will be permanently deleted from the case.
          <br/> This action cannot be undone.`
		};
		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().subscribe((okClicked: boolean) => {
			if (okClicked) {
				this.selectedJob$.next(null);
				this.productionJobSdk.deleteProductionJob(job.productionJobId).subscribe((success) => {
					if (success)
						this.productionJobs.splice(this.productionJobs.indexOf(job), 1);
				});
			}
		});
	}

	public getUserName(userId: string): string {
		if (this.users.findIndex(u => u.id === userId) < 0) return '';

		return this.users.find(u => u.id === userId).userName;
	}

	public isJobProcessingOrDone(job: ProductionJob): boolean {
		return job.status == this.productionJobRunStatus.Queued || job.status == this.productionJobRunStatus.Running || job.status == this.productionJobRunStatus.PostRun;
	}

	public addProductionToQueue(job: ProductionJob) {
		if (this.isProcessing) {
			this.userAlert = 'Unable to start production while processing is running.';
			return;
		}

		this.dialog.open(RunProductionDialogComponent).afterClosed().subscribe(response => {
			if (!response) return;

			if (response.isNotifyUser)
				this.notificationHubSdk.toggleNotifyWhenCaseCompletesProcessing().subscribe();

			const request = new RunProductionJobRequest();
			request.stepParams = { productionJobId: job.productionJobId };
			this.processingManager.scheduleStep({ stepRequest: request }).subscribe(() => {
				job.status = ProductionJobStatus.Queued;
				job.runById = this.userConfig.userId;
				job.dateTimeRun = new Date().toISOString();
				this.setJob(job);
				this.userAlert = 'You can continue to work while the Production is being created. The time to create a Production depends on the number of documents. The Production will be available for download from this page.';
			});
		});
	}

	public operationsConvertToTiff() {
		if (!this.selectedJob) {
			this.userAlert = 'No job selected.';
			return;
		}

		const argument = JSON.stringify({
			productionJobId: `${this.selectedJob.productionJobId}`
		});

		const step = new ProcessingQueueRequest();
		step.functionId = this.convertToTiffProductionFunctionId;
		step.functionArgument = argument;
		this.processingService.addIncomingFunction(step).subscribe(next => {
			if (next) {
				this.userAlert = `Converting Production to Tiff Production.`;
				this.productionJobs.find(p => p.productionJobId === this.selectedJob.productionJobId).status = ProductionJobStatus.Queued;
			}
		});
	}

	public operationsDownload() {
		if (!this.selectedJob) {
			this.userAlert = 'No job selected.';
			return;
		}

		this.dialog.open(OperationsProductionDownloadDialogComponent).afterClosed().subscribe(response => {
			if (!response) return;
			if (!response.downloadPath) {
				this.userAlert = 'Error in folder path';
				return;
			}
			const argument = JSON.stringify({
				productionFolder: `${response.downloadPath}`,
				productionJobId: `${this.selectedJob.productionJobId}`,
				userId: `${this.userConfig.userId}`
			});

			const step = new ProcessingQueueRequest();
			step.functionId = response.functionId;
			step.functionArgument = argument;
			this.processingService.addIncomingFunction(step).subscribe(next => {
				if (next)
					this.userAlert = `Download started: ${response.downloadPath}`;
			});
		});
	}

	public formatPathString(path: string, uri: string): string {
		const tempFolderPath = path.toLowerCase().replace(uri, '');
		return [tempFolderPath.slice(0, 1), ':', tempFolderPath.slice(1, tempFolderPath.length)].join('');
	}

	public evaluateProcessing() {
		this.subscription.add(this.store.select(selectIsProcessing).subscribe(isProcessing => {
			this.isProcessing = isProcessing;
			if (!this.isProcessing)
				this.userAlert = null;

		}));
	}

	public canUserDelete(job: ProductionJob): boolean {
		return job.createdById == this.userConfig.userId || this.userConfigService.inRoles(Role.AccountFeatureSet);
	}

	public formStatusUpdate($event: boolean) {
		this.isJobValid = $event;
	}

	isJobFormValid(job: ProductionJob): boolean {
		if (!this.selectedJob || job.productionJobId != this.selectedJob.productionJobId)
			return false;

		if (this.userConfigService.inRoles(Role.EditFeatureSet, Role.Administrator))
			return this.isJobValid;

		return false;
	}

	public openDownloadProductionPartDialog(productionJob: ProductionJob) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.disableClose = true;
		dialogConfig.data = {
			productionJob: productionJob
		};
		this.dialog.open(DownloadProductionDialogComponent, dialogConfig);
	}

	public exportAllJobsToLog() {
		this.productionJobSdk.exportAllProductionJobs().subscribe((url) => {
			window.open(url, '_blank');
		});
	}

	public selectedRangeEvent(event: SelectedRangeEvent) {
		this.selectedJob$.next(event.productionJob);
	}

	public createProductionEvent(job: ProductionJob): void {
		this.productionJobs.unshift(job);
		this.selectedJob$.next(job);
	}

	public setViewAllBatesRangesMode(): void {
		this.selectedJob$.next(null);
		this.showCreateProductionView = false;
		this.showBatesRangeView = true;
	}

	public completeValidation(isComplete: boolean) {
		this.validateProduction = false;
		if (isComplete) {
			this.selectedJob.status = this.productionJobRunStatus.PreRun;
			this.updateJob(this.selectedJob);
		}
	}

	public exportWithheldLog(job: ProductionJob): void {
		this.productionWithheldManager.getFiles(job.productionJobId, 'outgoing-production-withheld-log').subscribe((response) => {
			const dialogConfig = new MatDialogConfig();
			dialogConfig.autoFocus = true;
			dialogConfig.width = '500px';
			dialogConfig.data = {
				files: response,
				productionJob: job
			};
			dialogConfig.panelClass = "more-margin-top-panel";
			const dialogRef = this.dialog.open(ProductionWithheldLogDialogComponent, dialogConfig);
			dialogRef.afterClosed().subscribe((response: boolean) => {
				if (response)
					this.matSnackBar.open('Queued withheld export to Excel file successfully', 'X', {
						duration: 3000
					});
			});
		});
	}

	public downloadWithheld(job: ProductionJob): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = {
			productionJob: job
		};
		this.dialog.open(DownloadProductionWithheldDialogComponent, dialogConfig).afterClosed().subscribe();
	}

	private initializeProductionJobs() {
		const productionJobObservable = this.productionJobSdk.get();
		const productionUsersObservable = this.userManager.getProductionJobUsers();
		combineLatest([productionJobObservable, productionUsersObservable])
			.pipe(take(1))
			.subscribe(([productionJobs, users]) => {
				this.productionJobs = productionJobs
					.filter(p => p.lifecycle === 0)
					.sort((a: ProductionJob, b: ProductionJob) => a.dateTimeCreated < b.dateTimeCreated ? 1 : -1);
				this.users = users;

				let job: ProductionJobView = null;

				if (this.activatedRoute.snapshot.queryParams['ProductionJobId']) {
					job = this.productionJobs.find(j => j.productionJobId == this.activatedRoute.snapshot.queryParams['ProductionJobId']);
				} else if (this.selectedJob) {
					job = this.productionJobs.find(j => j.productionJobId == this.selectedJob.productionJobId);
				}
				if (job) {
					this.selectedJob$.next(job);
				}
			});
	}


	private setJob(job: ProductionJobView) {
		if (!job) {
			this.router.navigate([], { relativeTo: this.activatedRoute });
			this.selectedJob = null;
			return;
		}

		this.showCreateProductionView = false;
		this.showBatesRangeView = false;
		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams: { ProductionJobId: job.productionJobId }
		});
		this.selectedJob = job;
	}

	private updateJob(job: ProductionJobView) {
		this.productionJobSdk.updateProductionJob(job)
			.pipe(
				switchMap((jobResponse: ProductionJob) => {
					const idx = this.productionJobs.findIndex(p => p.productionJobId === job.productionJobId);
					this.productionJobs[idx] = jobResponse;
					return of(jobResponse);
				}))
			.subscribe();
		this.editJobId = '';
		this.setJob(job);
	}
}
